/* =============================================================
   PROCESS SECTION — 4-step "How It Works"
   Addresses: Original site had NO process explanation — visitors didn't know what to expect
   Design: Dark navy background, gold step numbers, connecting line
   ============================================================= */

import { MessageSquare, FileSearch, Rocket, BarChart2 } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Free Consultation",
    description: "Book a 30-minute call with Agnieszka. We'll discuss your business, current pain points, and exactly what you need. No obligation, no pressure.",
    timeframe: "Day 1",
  },
  {
    number: "02",
    icon: FileSearch,
    title: "Tailored Proposal",
    description: "Within 24 hours, you'll receive a clear proposal with the right package for your business and a transparent monthly fee. No hidden costs.",
    timeframe: "Within 24 hrs",
  },
  {
    number: "03",
    icon: Rocket,
    title: "Seamless Onboarding",
    description: "We handle the Xero setup, connect your bank feeds, and get everything organised. Most clients are fully onboarded within one week.",
    timeframe: "Week 1",
  },
  {
    number: "04",
    icon: BarChart2,
    title: "Ongoing Peace of Mind",
    description: "Weekly reconciliations, monthly reports, and proactive communication. You'll always know where your business stands financially.",
    timeframe: "Ongoing",
  },
];

export default function ProcessSection() {
  return (
    <section className="py-20 lg:py-28 bg-[oklch(0.22_0.06_250)]">
      <div className="container">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="section-label mb-4">Getting Started</div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            From First Call to{" "}
            <span className="italic text-[oklch(0.72_0.12_75)]">Financial Clarity</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
            Getting started is simple. Here's exactly what happens when you reach out to us.
          </p>
        </div>

        {/* Steps */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden lg:block absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-[oklch(0.72_0.12_75)/0.4] to-transparent" />

          {steps.map((step, index) => (
            <div key={step.number} className="relative flex flex-col items-start lg:items-center text-left lg:text-center">
              {/* Step icon circle */}
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-[oklch(0.30_0.07_250)] border-2 border-[oklch(0.72_0.12_75)/0.3] flex items-center justify-center">
                  <step.icon size={28} className="text-[oklch(0.72_0.12_75)]" />
                </div>
                {/* Step number badge */}
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[oklch(0.72_0.12_75)] flex items-center justify-center">
                  <span className="text-[oklch(0.15_0.04_250)] text-xs font-bold" style={{ fontFamily: "'DM Mono', monospace" }}>
                    {step.number}
                  </span>
                </div>
              </div>

              {/* Timeframe badge */}
              <div className="inline-flex items-center gap-1.5 bg-[oklch(0.72_0.12_75)/0.12] rounded-full px-3 py-1 mb-3">
                <span className="text-[oklch(0.72_0.12_75)] text-xs font-medium" style={{ fontFamily: "'DM Mono', monospace" }}>
                  {step.timeframe}
                </span>
              </div>

              <h3 className="text-white text-lg font-semibold mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {step.title}
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <a href="#contact" className="btn-gold text-base">
            Start with a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
